import { slicedToArray as _slicedToArray } from '../_virtual/_rollupPluginBabelHelpers.esm.js';
import { PlacardImageCarousel } from '@costar-ui/carousel';
import { Button, PliableImage } from '@costar-ui/components';
import { IconArrowThinLeft } from '@costar-ui/icons/csg/IconArrowThinLeft';
import { IconArrowThinRight } from '@costar-ui/icons/csg/IconArrowThinRight';
import React, { useState, useCallback, useMemo } from 'react';
import { useSpring, animated } from 'react-spring';

var NewsGallery = function NewsGallery(_ref) {
  var media = _ref.media;
  var _useState = useState(media[0]),
    _useState2 = _slicedToArray(_useState, 2),
    galleryItem = _useState2[0],
    setGalleryItem = _useState2[1];
  var handleOnClick = useCallback(function (item) {
    return function () {
      setGalleryItem(item);
    };
  }, []);
  var galleryItems = media.map(function (galleryItem) {
    return /*#__PURE__*/React.createElement("li", {
      key: galleryItem.caption,
      className: "inline-block h-full w-[calc(25%-15px)]"
    }, /*#__PURE__*/React.createElement(Button, {
      onPress: handleOnClick(galleryItem),
      className: "size-full border-0 bg-action-base-hover object-cover p-0",
      variant: "ghost-neutral"
    }, /*#__PURE__*/React.createElement(PliableImage, {
      alt: galleryItem.caption,
      src: galleryItem.image,
      className: "size-full cursor-pointer object-cover"
    })));
  });
  var pages = Math.ceil(media.length / 4);
  var exhibitStyleLeft = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    boxSizing: 'border-box',
    userSelect: 'none',
    transform: 'translateX(0%)'
  };
  var exhibitStyleRight = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    boxSizing: 'border-box',
    userSelect: 'none',
    transform: 'translateX(100%)'
  };
  var leftOnBack = useMemo(function () {
    return {
      from: {
        transform: 'translateX(-100%)'
      },
      to: {
        transform: 'translateX(0%)'
      }
    };
  }, []);
  var rightOnBack = useMemo(function () {
    return {
      from: {
        transform: 'translateX(0%)'
      },
      to: {
        transform: 'translateX(100%)'
      }
    };
  }, []);
  var leftOnForward = useMemo(function () {
    return {
      from: {
        transform: 'translateX(0%)'
      },
      to: {
        transform: 'translateX(-100%)'
      }
    };
  }, []);
  var rightOnForward = useMemo(function () {
    return {
      from: {
        transform: 'translateX(100%)'
      },
      to: {
        transform: 'translateX(0%)'
      }
    };
  }, []);
  var leftButtonIcon = /*#__PURE__*/React.createElement(IconArrowThinLeft, {
    size: 24,
    color: "#757575"
  });
  var rightButtonIcon = /*#__PURE__*/React.createElement(IconArrowThinRight, {
    size: 24,
    color: "#757575"
  });
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    forward = _React$useState2[0],
    setForward = _React$useState2[1];
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    active = _React$useState4[0],
    setActive = _React$useState4[1];
  var prev = (pages + active - 1) % pages;
  var current = (pages + active) % pages;
  var next = (pages + active + 1) % pages;

  // initial left position
  var _useSpring = useSpring(function () {
      return {
        from: exhibitStyleLeft
      };
    }, []),
    _useSpring2 = _slicedToArray(_useSpring, 2),
    left = _useSpring2[0],
    leftApi = _useSpring2[1];

  // initial right position
  var _useSpring3 = useSpring(function () {
      return {
        from: exhibitStyleRight
      };
    }, []),
    _useSpring4 = _slicedToArray(_useSpring3, 2),
    right = _useSpring4[0],
    rightApi = _useSpring4[1];
  var onBack = React.useCallback(function () {
    setActive(prev);
    setForward(false);
    leftApi.start(leftOnBack);
    rightApi.start(rightOnBack);
  }, [setActive, prev, leftApi, rightApi, leftOnBack, rightOnBack]);
  var onForward = React.useCallback(function () {
    setActive(next);
    setForward(true);
    leftApi.start(leftOnForward);
    rightApi.start(rightOnForward);
  }, [setActive, next, leftApi, rightApi, leftOnForward, rightOnForward]);
  var renderContentFor = React.useCallback(function (index) {
    var startIndex = index * 4;
    var endIndex = startIndex + 4;
    return /*#__PURE__*/React.createElement("ul", {
      className: "h-full space-x-5"
    }, galleryItems.slice(startIndex, endIndex));
  }, [galleryItems]);
  var _React$useMemo = React.useMemo(function () {
      return !pages ? [] : forward ? [renderContentFor(prev), renderContentFor(current)] : [renderContentFor(current), renderContentFor(next)];
    }, [pages, forward, renderContentFor, prev, current, next]),
    _React$useMemo2 = _slicedToArray(_React$useMemo, 2),
    leftContent = _React$useMemo2[0],
    rightContent = _React$useMemo2[1];
  var captionHtml = useMemo(function () {
    var _galleryItem$caption;
    return {
      __html: (_galleryItem$caption = galleryItem.caption) !== null && _galleryItem$caption !== void 0 ? _galleryItem$caption : ''
    };
  }, [galleryItem.caption]);
  var imageUrls = media.map(function (item) {
    return {
      src: item.image,
      caption: item.caption
    };
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "relative hidden space-y-6 lg:block"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col"
  }, /*#__PURE__*/React.createElement(PliableImage, {
    className: "size-full",
    src: galleryItem.image
  }), /*#__PURE__*/React.createElement("div", {
    className: "mt-[8px] flex flex-col self-stretch font-sans text-p-14 font-light leading-6 text-[#757575]"
  }, /*#__PURE__*/React.createElement("figcaption", {
    dangerouslySetInnerHTML: captionHtml
  }))), /*#__PURE__*/React.createElement("div", {
    className: "relative px-11"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative overflow-hidden pb-[16.67%]"
  }, /*#__PURE__*/React.createElement(animated.div, {
    style: left
  }, leftContent), /*#__PURE__*/React.createElement(animated.div, {
    style: right
  }, rightContent)), /*#__PURE__*/React.createElement(Button, {
    className: "absolute left-0 top-[calc(50%-12px)] size-6 cursor-pointer border-0",
    variant: "ghost-secondary",
    onPress: onBack
  }, leftButtonIcon), /*#__PURE__*/React.createElement(Button, {
    className: "absolute right-0 top-[calc(50%-12px)] size-6 cursor-pointer border-0",
    variant: "ghost-secondary",
    onPress: onForward
  }, rightButtonIcon))), /*#__PURE__*/React.createElement("div", {
    className: "block lg:hidden"
  }, /*#__PURE__*/React.createElement(PlacardImageCarousel, {
    images: imageUrls,
    enableCaption: true
  })));
};

export { NewsGallery };
//# sourceMappingURL=gallery.esm.js.map
